import { onUnmounted } from "vue";

/**
 * How long to wait (ms) after a focus event before checking scroll position.
 * Gives the mobile keyboard time to begin appearing so we can read the
 * reduced visualViewport.height.
 */
const SETTLE_MS = 150;

/** Max-width at which BottomNav is visible (matches `md:hidden` breakpoint). */
const MOBILE_MAX_WIDTH = 768;

function scrollSafelyIntoView(el: HTMLElement): void {
  const vv = window.visualViewport;

  if (!vv) {
    // SSR / very old browser — basic fallback
    el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    return;
  }

  const rect = el.getBoundingClientRect();
  // offsetHeight == 0 when element has display:none (BottomNav is md:hidden on desktop)
  const navEl = document.querySelector<HTMLElement>('[data-testid="bottom-nav"]');
  const navHeight = navEl?.offsetHeight ?? 0;

  // Safe bottom edge: above keyboard (vv.height) and above BottomNav + 8px margin
  const safeBottom = vv.height - navHeight - 8;

  if (rect.bottom > safeBottom) {
    // Element (or its action row) is occluded — scroll up to bring it into safe area
    window.scrollBy({ top: rect.bottom - safeBottom, behavior: "smooth" });
  } else if (rect.top < vv.offsetTop) {
    // Element scrolled above the visible top
    el.scrollIntoView({ block: "start", behavior: "smooth" });
  }
}

/**
 * Attaches to an input's focus/blur events and ensures the element stays
 * visible above the on-screen keyboard and the fixed BottomNav.
 *
 * Desktop (≥ 768px) is a no-op — no on-screen keyboard exists there.
 *
 * Watch edge cases:
 *  W1 — N/A: focus-event driven, not Vue watch (no immediate path needed)
 *  W2 — N/A: each focus is independent, no consumed-guard logic
 *  W3 — N/A
 *  W4 — handled: onUnmounted(detach) cancels timer + removes visualViewport listener
 *  W5 — handled: clearTimer() at the start of scheduleScroll cancels any pending call
 */
export function useKeyboardAwareFocus() {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let activeEl: HTMLElement | null = null;
  let vpListener: (() => void) | null = null;

  function clearTimer(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function scheduleScroll(el: HTMLElement): void {
    clearTimer(); // W5: cancel any pending scroll from a prior focus event
    activeEl = el;
    timer = setTimeout(() => {
      timer = null;
      if (activeEl) scrollSafelyIntoView(activeEl);
    }, SETTLE_MS);
  }

  function onFocusInput(e: FocusEvent): void {
    // Desktop guard — no on-screen keyboard, leave layout untouched
    if (window.innerWidth >= MOBILE_MAX_WIDTH) return;

    const el = e.currentTarget as HTMLElement;

    // Attach visualViewport listener once per active focus session
    if (window.visualViewport && !vpListener) {
      vpListener = () => {
        // Keyboard resize fires multiple times during animation (W5: scheduleScroll
        // cancels the prior pending timer each time)
        if (activeEl) scheduleScroll(activeEl);
      };
      window.visualViewport.addEventListener("resize", vpListener);
    }

    scheduleScroll(el);
  }

  function detach(): void {
    clearTimer(); // W4: cancel pending timer
    activeEl = null;
    if (vpListener && window.visualViewport) {
      window.visualViewport.removeEventListener("resize", vpListener); // W4
      vpListener = null;
    }
  }

  onUnmounted(detach); // W4: clean up if component unmounts while field is focused

  return { onFocusInput, onBlurInput: detach };
}
